import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Users } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DashboardLayout from "@/components/DashboardLayout";
import { Empty, FadeIn } from "@/components/Shared";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function DirectoryContent() {
    const [faculty, setFaculty] = useState(null);

    useEffect(() => {
        api.get("/faculty").then((r) => setFaculty(r.data)).catch(() => setFaculty([]));
    }, []);

    return (
        <div className="mx-auto max-w-7xl" data-testid="faculty-directory">
            <h1 className="font-display text-3xl font-bold tracking-tight">Faculty Network</h1>
            <p className="mt-2 text-base text-muted-foreground">Meet the people behind the projects. Verified profiles have identity and institutional affiliation checked.</p>
            {faculty === null ? <p className="mt-8 text-sm text-muted-foreground">Loading faculty...</p>
            : faculty.length === 0 ? <div className="mt-8"><Empty icon={Users} title="No faculty yet" hint="Faculty members are being onboarded." /></div>
            : (
                <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {faculty.map((f, i) => (
                        <FadeIn key={f.id} delay={i * 0.05}>
                            <Link to={`/faculty/${f.id}`} data-testid={`faculty-card-${f.id}`} className="card-soft group block h-full p-7 transition-transform duration-200 hover:-translate-y-1">
                                <div className="flex items-center gap-4">
                                    <img src={f.picture} alt={f.name} className="h-16 w-16 rounded-full object-cover" />
                                    <div>
                                        <p className="font-display text-lg font-semibold group-hover:text-primary">{f.name}</p>
                                        <p className="text-xs text-muted-foreground">{f.profile?.title} · {f.profile?.institution}</p>
                                    </div>
                                </div>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {(f.profile?.research_areas || []).map((a) => (
                                        <span key={a} className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">{a}</span>
                                    ))}
                                </div>
                                <div className="mt-5 flex items-center justify-between border-t border-border/40 pt-4 text-xs text-muted-foreground">
                                    <span>{f.projects_count} projects · {f.internships_count} internships</span>
                                    {f.verified
                                        ? <span className="flex items-center gap-1 font-semibold text-tealx" data-testid={`verified-badge-${f.id}`}><BadgeCheck className="h-4 w-4" /> Verified</span>
                                        : <span className="text-muted-foreground/60">Verification pending</span>}
                                </div>
                            </Link>
                        </FadeIn>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function FacultyDirectory() {
    const { user } = useAuth();
    if (user) return <DashboardLayout title="Faculty Network"><DirectoryContent /></DashboardLayout>;
    return (
        <div className="bg-background">
            <Header />
            <div className="px-6 py-14"><DirectoryContent /></div>
            <Footer />
        </div>
    );
}
